import { useMutation } from '@tanstack/react-query';
import { Modal, ModalBody, ModalHeader } from 'flowbite-react';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';
import { InlineWidget } from 'react-calendly';
import { useOutletContext } from 'react-router';

import { mutations } from 'src/api/mutations';

import { ContainerOutletContext } from './container';
import EmailRow from './email-row';

export function OptimizerLiteGenerations() {
  const {
    optimizerLiteContext: { liteGenerationGroup },
    setOptimizerLiteContext,
  } = useOutletContext<ContainerOutletContext>();
  const posthog = usePostHog();
  const [openModal, setOpenModal] = useState(false);
  const [generationinProgress, setGenerationInProgress] = useState(false);
  const [variantContents, setVariantContents] = useState([
    liteGenerationGroup.variant1Content as { subject: string; preview: string },
    liteGenerationGroup.variant2Content as { subject: string; preview: string },
    liteGenerationGroup.variant3Content as { subject: string; preview: string },
  ]);
  const [loadingDots, setLoadingDots] = useState('');

  const { mutateAsync: triggerVariantGeneration } = useMutation({
    ...mutations.triggerVariantGeneration,
    onSuccess: (newLiteGenerationGroup) => {
      setOptimizerLiteContext((prevContext) => ({
        ...prevContext,
        liteGenerationGroup: newLiteGenerationGroup,
      }));
      const newVariantContents = [
        newLiteGenerationGroup.variant1Content as { subject: string; preview: string },
        newLiteGenerationGroup.variant2Content as { subject: string; preview: string },
        newLiteGenerationGroup.variant3Content as { subject: string; preview: string },
      ];
      setVariantContents((variantContents) => [...variantContents, ...newVariantContents]);
    },
  });

  const logEventToPosthog = useCallback(
    (eventName: string) => {
      const posthogData = {
        email: liteGenerationGroup.customerEmailAddress,
      };
      posthog.capture(eventName, posthogData);
    },
    [posthog, liteGenerationGroup.customerEmailAddress],
  );

  const launchVariantGeneration = useCallback(async () => {
    if (generationinProgress) return;
    const animateDots = () => {
      setLoadingDots((dots) => (dots.length >= 3 ? '' : dots + '.'));
      setTimeout(animateDots, 1000);
    };
    setGenerationInProgress(true);
    animateDots();
    await triggerVariantGeneration([liteGenerationGroup.id]);
    setGenerationInProgress(false);
    setLoadingDots('');
  }, [setGenerationInProgress, generationinProgress, liteGenerationGroup, triggerVariantGeneration]);

  return (
    <>
      <div className='flex min-h-[calc(100vh-5rem)] items-center justify-center'>
        <main className='relative size-full overflow-y-auto bg-white p-0 lg:ml-3'>
          <div className='m-0 mt-20'>
            <h2 className='mb-6 text-center text-2xl text-gray-900'>
              <span className='font-normal'>Here are your awesome</span>{' '}
              <span className='font-semibold'>subject lines</span> <span className='text-gray-400'> - previews</span>
            </h2>

            <div className='relative overflow-x-auto pb-8 pr-0'>
              <table className='w-full text-left text-gray-500'>
                <tbody className='bg-white dark:bg-gray-800'>
                  {variantContents.map((oneGeneration: { subject: string; preview: string }, i: number) => {
                    const { subject, preview } = oneGeneration;
                    return (
                      <tr key={i} className='border-y border-gray-200'>
                        <EmailRow subject={subject} preview={preview} index={i} />
                      </tr>
                    );
                  })}
                  <tr>
                    <td className='px-6 py-4 text-left'>
                      <button
                        onClick={() => {
                          logEventToPosthog('optimizerLiteGenerateDifferentOptionsClicked');
                          void launchVariantGeneration();
                        }}
                        className='rounded-lg border border-blue-500 bg-white px-5 py-2.5 text-sm font-medium text-black hover:underline focus:underline'
                      >
                        {generationinProgress ? `Generating${loadingDots}` : 'Generate different options'}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className='mb-6 flex justify-center gap-8 px-5'>
              <button
                className='rounded-lg bg-linear-to-br from-purple-600 to-blue-500 px-8 py-2.5 text-base font-medium text-white hover:bg-linear-to-bl focus:outline-none focus:ring-4 focus:ring-blue-300'
                onClick={() => {
                  logEventToPosthog('optimizerLiteStartKlaviyoClicked');
                  setOpenModal(true);
                }}
              >
                Start Klaviyo A/B test
                <br />
                and Auto Optimize Email
              </button>
              <button
                className='rounded-lg border border-blue-500 bg-white px-8 py-2.5 text-base font-medium text-black hover:underline focus:underline'
                onClick={() => {
                  logEventToPosthog('optimizerLiteGenerateForOtherEmailsClicked');
                  window.location.replace('/optimizer-lite/dashboard');
                }}
              >
                Generate subject lines
                <br />
                and previews for other emails
              </button>
            </div>
          </div>
        </main>
      </div>

      <Modal
        dismissible
        show={openModal}
        onClose={() => {
          setOpenModal(false);
        }}
      >
        <ModalHeader>
          This feature and more are available on Optimizer Pro. <br />
          Let’s chat!
        </ModalHeader>
        <ModalBody>
          <InlineWidget url='https://calendly.com/d/3kh-hk8-mr3/maverick-demo' />
        </ModalBody>
      </Modal>
    </>
  );
}
